// What to call a project.
//
// `projects.name` is `text NOT NULL`, which makes it look safe — but NOT NULL is
// not the same as non-empty, and a project is created the moment a client starts
// talking, well before Mira has anything to name it after. So a large share of
// real rows carry an empty name, and rendering `name` directly gives a console
// full of identical "Untitled" rows that an operator cannot tell apart.
//
// That is the bug this fixes: not a missing field, a field that is legitimately
// blank for a while. The fallbacks below are ordered by how much they tell a
// human, and the last one is deliberately still identifying (a short id) rather
// than a placeholder word.

/** The fields a title can be derived from. Both list shapes and the detail
 *  payload satisfy this, so one helper covers every surface. */
export interface TitleableProject {
  id: string;
  name?: string | null;
  summary?: string | null;
}

/**
 * Names that are technically set but say nothing.
 *
 * `create_project` (backend/recruiter/repo/projects.py) defaults a blank name to
 * the literal "Untitled Project", so a lot of rows carry a real, non-empty name
 * that is still just a placeholder. Treating those as unnamed is the difference
 * between a table of distinguishable projects and a wall of identical rows.
 *
 * Compared case-insensitively and trimmed. Keep this in step with the backend
 * default: if that string changes, this list must change with it.
 */
const PLACEHOLDER_NAMES = new Set(["untitled project", "untitled", "new project"]);

function isPlaceholder(name: string): boolean {
  return PLACEHOLDER_NAMES.has(name.toLowerCase());
}

/** A short, human-readable stub of a project id — enough to tell two untitled
 *  rows apart and to paste into a query. */
export function shortProjectId(id: string): string {
  return id.replace(/-/g, "").slice(0, 8);
}

/**
 * The best available name for a project:
 *
 *   1. `name`, when it is actually set;
 *   2. its `summary`, trimmed to a title-ish length — Mira writes this early,
 *      so it is usually populated when the name is not;
 *   3. `Project <short id>`, which is at least unique and greppable.
 *
 * Never returns an empty string, and never returns the same text for two
 * different projects.
 */
export function projectTitle(project: TitleableProject): string {
  const name = project.name?.trim();
  if (name && !isPlaceholder(name)) return name;

  const summary = project.summary?.trim();
  if (summary) {
    // One line, and short enough to sit in a table cell without wrapping. The
    // full summary still renders underneath the title, so nothing is lost.
    const firstLine = summary.split("\n")[0].trim();
    return firstLine.length > 60 ? `${firstLine.slice(0, 57).trimEnd()}…` : firstLine;
  }

  return `Project ${shortProjectId(project.id)}`;
}

/** True when the title had to be derived — the UI marks those rows so an
 *  operator knows they are looking at a stand-in, not a name someone chose. */
export function isDerivedTitle(project: TitleableProject): boolean {
  const name = project.name?.trim();
  return !name || isPlaceholder(name);
}
